import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Plus, X } from 'lucide-react';

const EducationForm = ({ data, onSave }) => {
  const [schools, setSchools] = useState(data && data.length > 0 ? data : [{
    school_name: '',
    city: '',
    state: '',
    start_date: '',
    end_date: '',
    gpa: '',
    gpa_scale: '4.0',
    class_rank: '',
    class_size: ''
  }]);

  const handleChange = (index, field, value) => {
    const updated = [...schools];
    updated[index][field] = value;
    setSchools(updated);
  };

  const addSchool = () => {
    setSchools([...schools, {
      school_name: '',
      city: '',
      state: '',
      start_date: '',
      end_date: '',
      gpa: '',
      gpa_scale: '4.0',
      class_rank: '',
      class_size: ''
    }]);
  };

  const removeSchool = (index) => {
    if (schools.length > 1) {
      setSchools(schools.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(schools);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {schools.map((school, index) => (
        <Card key={index} className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              {index === 0 ? 'Current/Most Recent School' : `School ${index + 1}`}
            </h3>
            {schools.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeSchool(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor={`school_name-${index}`}>School Name *</Label>
              <Input
                id={`school_name-${index}`}
                value={school.school_name}
                onChange={(e) => handleChange(index, 'school_name', e.target.value)}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor={`city-${index}`}>City *</Label>
                <Input
                  id={`city-${index}`}
                  value={school.city}
                  onChange={(e) => handleChange(index, 'city', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor={`state-${index}`}>State/Province *</Label>
                <Input
                  id={`state-${index}`}
                  value={school.state}
                  onChange={(e) => handleChange(index, 'state', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor={`start_date-${index}`}>Start Date *</Label>
                <Input
                  id={`start_date-${index}`}
                  type="month"
                  value={school.start_date}
                  onChange={(e) => handleChange(index, 'start_date', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor={`end_date-${index}`}>End Date (or Expected) *</Label>
                <Input
                  id={`end_date-${index}`}
                  type="month"
                  value={school.end_date}
                  onChange={(e) => handleChange(index, 'end_date', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor={`gpa-${index}`}>GPA *</Label>
                <Input
                  id={`gpa-${index}`}
                  type="number"
                  step="0.01"
                  value={school.gpa}
                  onChange={(e) => handleChange(index, 'gpa', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor={`gpa_scale-${index}`}>GPA Scale *</Label>
                <Input
                  id={`gpa_scale-${index}`}
                  type="number"
                  step="0.1"
                  value={school.gpa_scale}
                  onChange={(e) => handleChange(index, 'gpa_scale', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor={`class_rank-${index}`}>Class Rank (Optional)</Label>
                <Input
                  id={`class_rank-${index}`}
                  type="number"
                  value={school.class_rank}
                  onChange={(e) => handleChange(index, 'class_rank', e.target.value)}
                  placeholder="e.g., 25"
                />
              </div>
              <div>
                <Label htmlFor={`class_size-${index}`}>Class Size (Optional)</Label>
                <Input
                  id={`class_size-${index}`}
                  type="number"
                  value={school.class_size}
                  onChange={(e) => handleChange(index, 'class_size', e.target.value)}
                  placeholder="e.g., 400"
                />
              </div>
            </div>
          </div>
        </Card>
      ))}

      <Button type="button" variant="outline" onClick={addSchool} className="w-full">
        <Plus className="h-4 w-4 mr-2" />
        Add Another School
      </Button>

      <div className="flex justify-end">
        <Button type="submit">Save & Continue</Button>
      </div>
    </form>
  );
};

export default EducationForm;